import { useMemo, useState } from 'react';

import Search from '@mui/icons-material/Search';
import {
  Box,
  Container,
  InputAdornment,
  Paper,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { Link } from 'react-router';

import { getOr } from '@/api/client';
import { RUTAS } from '@/api/rutas';
import { MedirVisita } from '@/components/MedirVisita';
import { migasDePan } from '@/lib/datos-estructurados';
import { SIN_RUBRO, slugDeRubro } from '@/lib/rubros';

import type { Route } from './+types/rubros';

export function meta(): Route.MetaDescriptors {
  return [
    { title: 'Rubros de repuestos para motos | CKING' },
    {
      name: 'description',
      content:
        'Todos los rubros de repuestos para motos que importa y distribuye CKING: árbol de ' +
        'levas, carburadores, frenos, transmisión, espejos y más. Entrá al rubro y mirá los ' +
        'productos con su código.',
    },
    { tagName: 'link', rel: 'canonical', href: 'https://cking.com.ar/rubros' },
    { property: 'og:title', content: 'Rubros de repuestos para motos | CKING' },
  ];
}

export interface Rubro {
  nombre: string;
  total: number;
  /** La foto que representa al rubro, elegida por el servidor. Null si el rubro no tiene ninguna. */
  imagen: string | null;
  /** La dirección de su página: `arbol-de-levas`. */
  slug: string;
}

/** Lo que devuelve la API, con los nombres que usa la API. */
interface TarjetaDeLaApi {
  name: string;
  products: number;
  image: string | null;
}

/**
 * Los rubros, en el orden en que se muestran.
 *
 * El orden se decide acá y no en el servidor porque es alfabético en español: la Ñ va después de
 * la N y los acentos no cambian el lugar, y eso lo sabe el navegador de quien mira.
 *
 * "SIN CATEGORIA" no entra: es el cajón donde caen los productos a los que la planilla no les
 * puso título de rubro. Darle una página sería publicar el nombre de un cajón interno.
 */
export function ordenarRubros(tarjetas: TarjetaDeLaApi[]): Rubro[] {
  return tarjetas
    .filter(t => t.name.trim().toUpperCase() !== SIN_RUBRO)
    .map(t => ({ nombre: t.name, total: t.products, imagen: t.image, slug: slugDeRubro(t.name) }))
    .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
}

/**
 * El índice de rubros.
 *
 * Una sola consulta, y viene con todo hecho: el nombre, la cuenta y la foto de cada rubro.
 *
 * Antes esta pantalla bajaba el catálogo completo para agruparlo acá, y después cada tarjeta
 * pedía las fotos de los cinco primeros productos de su rubro hasta que alguna contestara. Eran
 * unos quinientos pedidos para una pantalla, contra una API que corta a 30 por segundo; los que
 * volvían 429 dejaban la tarjeta sin foto como si el rubro no tuviera ninguna.
 *
 * Y además estaba mal. "1/2 JGO JUNTAS DE MOTOR" tiene veintinueve productos y diecisiete fotos,
 * y salía vacío porque los cinco que están primeros son justo los cinco sin foto. Cuál producto
 * tiene foto lo sabe la base en una consulta, así que lo decide ella.
 */
export async function loader({ request }: Route.LoaderArgs) {
  const { categories } = await getOr<{ categories: TarjetaDeLaApi[] }>(
    RUTAS.catalogo.categoriasEnTarjetas,
    { categories: [] },
    { request },
  );

  return { rubros: ordenarRubros(categories) };
}

export default function Rubros({ loaderData }: Route.ComponentProps) {
  const { rubros } = loaderData;
  const [filtro, setFiltro] = useState('');

  const visibles = useMemo(() => {
    const buscado = filtro.trim().toLocaleLowerCase('es');
    if (!buscado) return rubros;

    return rubros.filter(rubro => rubro.nombre.toLocaleLowerCase('es').includes(buscado));
  }, [rubros, filtro]);

  const piezas = rubros.reduce((suma, rubro) => suma + rubro.total, 0);

  return (
    <Box sx={{ py: 4, bgcolor: '#121212', minHeight: '100vh' }}>
      <MedirVisita path="/rubros" />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(migasDePan([{ nombre: 'Rubros', ruta: '/rubros' }])),
        }}
      />

      <Container maxWidth="xl">
        <Typography variant="h1" component="h1" sx={{ mb: 1 }}>
          Rubros de repuestos para motos
        </Typography>

        <Typography sx={{ color: 'text.secondary', mb: 4, maxWidth: 820 }}>
          {rubros.length} familias de repuestos y {piezas.toLocaleString('es-AR')} piezas que CKING
          importa y distribuye al por mayor en Argentina. Entrá a un rubro para ver los productos
          con su código, o usá el buscador del catálogo si ya sabés qué pieza necesitás.
        </Typography>

        <TextField
          fullWidth
          value={filtro}
          onChange={evento => setFiltro(evento.target.value)}
          label="Filtrar rubros"
          aria-label="Filtrar rubros por nombre"
          sx={{ mb: 4, maxWidth: 460 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            },
          }}
        />

        {visibles.length === 0 ? (
          <Typography role="status" sx={{ color: 'text.secondary', py: 6 }}>
            Ningún rubro se llama así.
          </Typography>
        ) : (
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            {visibles.map(rubro => (
              <Grid key={rubro.nombre} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <TarjetaDeRubro rubro={rubro} />
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
}

/**
 * Un rubro, con una pieza suya de muestra.
 *
 * La foto ya viene elegida y con su dirección: acá no se busca nada. Lo único que puede fallar es
 * que el archivo no esté, y para eso está `onError`.
 */
function TarjetaDeRubro({ rubro }: { rubro: Rubro }) {
  const [rota, setRota] = useState(false);
  const [cargada, setCargada] = useState(false);

  const hayFoto = rubro.imagen !== null && !rota;

  return (
    <Paper
      component={Link}
      to={`/rubros/${rubro.slug}`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: '#1e1e1e',
        border: '1px solid transparent',
        textDecoration: 'none',
        color: 'inherit',
        transition: 'transform .35s cubic-bezier(.22,.61,.36,1), border-color .35s ease',
        '&:hover': { transform: 'translateY(-4px)', borderColor: 'rgba(230,0,0,.35)' },
        '&:hover .foto-del-rubro': { transform: 'scale(1.05)' },
        '@media (prefers-reduced-motion: reduce)': {
          transition: 'border-color .35s ease',
          '&:hover': { transform: 'none' },
          '&:hover .foto-del-rubro': { transform: 'none' },
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          height: 150,
          m: 1,
          mb: 0,
          borderRadius: 1.5,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // Blanco como el resto de los recuadros de foto del sitio. Mientras la imagen baja
          // queda oscuro: un rectángulo blanco vacío parece una foto rota, no algo que carga.
          bgcolor: hayFoto && !cargada ? 'transparent' : '#fff',
        }}
      >
        {hayFoto && (
          <Box
            component="img"
            className="foto-del-rubro"
            src={rubro.imagen ?? undefined}
            alt={`Repuestos del rubro ${rubro.nombre}`}
            sizes="(max-width: 600px) 92vw, (max-width: 900px) 46vw, 280px"
            // El esqueleto se va cuando la imagen terminó de bajar, no cuando llegó su
            // dirección: entre las dos cosas hay un salto en el que el recuadro está vacío.
            loading="lazy"
            onLoad={() => setCargada(true)}
            onError={() => setRota(true)}
            sx={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              // Invisible pero con caja, y NO `display: none`. Una imagen sin caja no se carga
              // nunca: el navegador no tiene contra qué medir si entró en pantalla, así que no
              // dispara `onLoad` y el esqueleto se queda para siempre. Pasó exactamente eso.
              opacity: cargada ? 1 : 0,
              transition: 'opacity .25s ease, transform .45s cubic-bezier(.22,.61,.36,1)',
            }}
          />
        )}

        {hayFoto && !cargada && (
          <Skeleton
            variant="rectangular"
            animation="wave"
            // Encima de la imagen, no en su lugar: así la imagen existe, ocupa caja y se baja.
            sx={{ position: 'absolute', inset: 0 }}
          />
        )}
      </Box>

      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 0.5, flexGrow: 1 }}>
        <Typography
          component="h2"
          sx={{
            fontFamily: 'var(--font-roboto-condensed)',
            fontWeight: 700,
            fontSize: '1.15rem',
            lineHeight: 1.1,
            letterSpacing: '-0.01em',
          }}
        >
          {rubro.nombre}
        </Typography>

        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}
        >
          {rubro.total} {rubro.total === 1 ? 'producto' : 'productos'}
        </Typography>
      </Box>
    </Paper>
  );
}
